import {put, call} from 'redux-saga/effects';
import Deadline from "../../entities/deadline";
import * as Actions from "../actions/deadlineActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type DeadlineAction = {
    deadlineId: string;
    deadline: Deadline;
    deadlines: Deadline[];
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneDeadline(action: DeadlineAction) {
	const session = tabSession.captureGeneration();
	try {
		const deadline: Deadline = yield call(api.findOne, 'deadline', action.deadlineId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.DEADLINE_FETCH_ONE_SUCCESS, deadlineId: action.deadlineId, deadline});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.DEADLINE_FETCH_ONE_FAILURE, deadlineId: action.deadlineId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {DeadlineAction} action
 */
export function* saveDeadline(action: DeadlineAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ DEADLINE_SAVE saga', action.deadline?.id);
    
	if (action.deadline === undefined || action.deadline === null) {
		console.error('saveDeadline received a null');
		throw new Error('saveDeadline received a null');
	}

	if (!(action.deadline instanceof Deadline)) {
		action.deadline = new Deadline(action.deadline);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'deadline', action.deadline);
		if (addResult.isErr()) {
			console.error(`⮂ saveDeadline: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.DEADLINE_SAVE_FAILURE, deadline: action.deadline, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.DEADLINE_SAVE, deadline: action.deadline});

		// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
		// removes it when it saves; a draft typed somewhere else may be newer than this copy.
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.DEADLINE_SAVE_FAILURE, deadline: action.deadline, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {DeadlineAction} action
 * @param {Deadline[]} action.deadlines
 */
export function* saveManyDeadlines(action: DeadlineAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ DEADLINE_SAVE_MANY saga', action.deadlines.map(deadline => deadline.id).join(','));
    
    if (!Array.isArray(action.deadlines)) {
        console.error('saveManyDeadlines expected an array, received', action.deadlines);
        throw new Error('saveManyDeadlines expected an array');
    }

	try {
		const toSave: Deadline[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.deadlines.length; i++) {
			const deadline = action.deadlines[i];
			deadline.persisted = false;
			toSave.push(deadline);
		}

		const addResult = yield call(idb.add, 'deadline', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyDeadlines: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.DEADLINE_SAVE_MANY_FAILURE, deadlines: action.deadlines, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.DEADLINE_SAVE_MANY, deadlines: toSave});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.DEADLINE_SAVE_MANY_FAILURE, deadlines: action.deadlines, status: 500, err});

		console.error(`Error saving ${action.deadlines.length} deadlines to database`, err);
	}
}

/**
 * @param {DeadlineAction} action
 */
export function* removeDeadline(action: DeadlineAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
	if (!userId || action.deadline === undefined || action.deadline === null || !(action.deadline instanceof Deadline)) {
			console.error('removeDeadline expected type Deadline, received', action.deadline);
			throw new Error('removeDeadline expected type Deadline');
		}

	try {
		// Mark as deleted
		action.deadline.status = 'D';
		action.deadline.addedOn = newHRTimestamp();
		action.deadline.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'deadline', action.deadline);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeDeadline: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.DEADLINE_REMOVE_FAILURE, deadline: action.deadline, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.DEADLINE_REMOVE, deadline: action.deadline});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.DEADLINE_REMOVE_FAILURE, deadline: action.deadline, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Deadline.
	*
	* @param {DeadlineAction} action
	*/
export function* restoreDeadline(action: DeadlineAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreDeadline expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.deadline === undefined || action.deadline === null) {
        console.error('restoreDeadline expected a Deadline, received null');
        throw new Error('restoreDeadline expected a Deadline, received null');
    }
    else if (!(action.deadline instanceof Deadline)) action.deadline = new Deadline(action.deadline); 
    
	try {
		// Mark as restored
		action.deadline.status = 'U';
		action.deadline.addedOn = newHRTimestamp();
		action.deadline.addedBy = userId;
		action.deadline.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'deadline', action.deadline);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreDeadline: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.DEADLINE_RESTORE_FAILURE, deadline: action.deadline, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.DEADLINE_RESTORE, deadline: action.deadline});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.DEADLINE_RESTORE_FAILURE, deadline: action.deadline, status: 500, err});

		console.error('Error restoring from database', err);
	}
}